import React, { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useI18n, SUPPORTED, langPath } from "@/i18n/I18nContext";
import { Menu, X, Globe, Building2 } from "lucide-react";

function Logo({ lang }) {
  return (
    <Link
      to={langPath(lang)}
      className="flex items-center gap-2 shrink-0"
      aria-label="Zynteroq"
      data-testid="logo-link"
    >
      <span
        aria-hidden
        className="inline-flex items-center justify-center w-9 h-9 rounded-xl"
        style={{ background: "linear-gradient(135deg,#0F3B36,#0B6B5A)" }}
      >
        <Building2 size={18} color="#E7C989" />
      </span>
      <span
        className="font-display text-[22px] leading-none tracking-tight"
        style={{ color: "var(--z-ink)" }}
      >
        Zynteroq
      </span>
    </Link>
  );
}

export default function Header() {
  const { t, lang } = useI18n();
  const navigate = useNavigate();
  const location = useLocation();
  const [open, setOpen] = useState(false);

  const switchLang = (next) => {
    if (next === lang) return;
    // Compute new path preserving the sub-path after the current lang prefix
    let sub = "";
    const parts = location.pathname.split("/").filter(Boolean);
    if (parts.length && SUPPORTED.includes(parts[0])) {
      sub = "/" + parts.slice(1).join("/");
    } else if (parts.length) {
      sub = "/" + parts.join("/");
    }
    // Preserve hash so anchor stays
    const newPath = `/${next}${sub === "/" ? "" : sub}${location.hash || ""}`;
    navigate(newPath, { state: { langSwitch: true }, replace: false });
  };

  const navLinks = [
    { key: "how", to: langPath(lang) + "#how" },
    { key: "projects", to: langPath(lang) + "#projects" },
    { key: "about", to: langPath(lang) + "#about" },
    { key: "contact", to: langPath(lang) + "#contact" },
  ];

  return (
    <header className="z-header">
      <div className="z-container flex items-center justify-between h-[72px]">
        <Logo lang={lang} />

        {/* Desktop nav */}
        <nav className="hidden md:flex items-center gap-8" aria-label="primary">
          {navLinks.map((n) => (
            <a
              key={n.key}
              href={n.to}
              onClick={(e) => {
                // If we are already on the lang home, do a scroll-into-view
                if (location.pathname === langPath(lang) || location.pathname === langPath(lang) + "/") {
                  const id = n.to.split("#")[1];
                  const el = id && document.getElementById(id);
                  if (el) {
                    e.preventDefault();
                    el.scrollIntoView({ behavior: "smooth", block: "start" });
                  }
                }
              }}
              className="text-[14px] font-medium text-[color:var(--z-ink)] hover:text-[color:var(--z-brand)] transition-colors"
              data-testid={`nav-${n.key}`}
            >
              {t(`nav.${n.key}`)}
            </a>
          ))}
        </nav>

        {/* Right cluster */}
        <div className="flex items-center gap-3">
          <div className="hidden md:flex items-center gap-1 border border-[color:var(--z-line)] rounded-full p-1">
            <Globe size={14} className="ml-2 mr-1 opacity-60" />
            {SUPPORTED.map((code) => (
              <button
                key={code}
                type="button"
                onClick={() => switchLang(code)}
                data-testid={`lang-${code}`}
                className={`px-3 py-1.5 rounded-full text-[12px] font-semibold uppercase tracking-wider transition-colors ${
                  lang === code
                    ? "bg-[color:var(--z-ink)] text-[#F7F2E7]"
                    : "text-[color:var(--z-ink)] hover:bg-[rgba(14,27,26,0.06)]"
                }`}
                aria-pressed={lang === code}
              >
                {code}
              </button>
            ))}
          </div>

          <Link
            to={langPath(lang) + "#contact"}
            className="hidden md:inline-flex z-btn z-btn-primary"
            data-testid="header-cta"
          >
            {t("hero.ctaPrimary")}
          </Link>

          <button
            type="button"
            className="md:hidden inline-flex items-center justify-center w-10 h-10 rounded-full border border-[color:var(--z-line)]"
            onClick={() => setOpen((v) => !v)}
            aria-label={open ? t("nav.close") : t("nav.menu")}
            data-testid="mobile-menu-toggle"
          >
            {open ? <X size={18} /> : <Menu size={18} />}
          </button>
        </div>
      </div>

      {/* Mobile drawer */}
      {open && (
        <div className="md:hidden border-t border-[color:var(--z-line)] bg-[color:var(--z-bg)]" data-testid="mobile-menu">
          <div className="z-container py-5 flex flex-col gap-1">
            {navLinks.map((n) => (
              <a
                key={n.key}
                href={n.to}
                onClick={() => setOpen(false)}
                className="py-3 text-[15px] font-medium border-b border-[color:var(--z-line)]"
                data-testid={`mnav-${n.key}`}
              >
                {t(`nav.${n.key}`)}
              </a>
            ))}
            <div className="flex items-center justify-between mt-4">
              <span className="text-[12px] uppercase tracking-wider text-[color:var(--z-muted)]">
                {t("nav.language")}
              </span>
              <div className="flex items-center gap-1 border border-[color:var(--z-line)] rounded-full p-1">
                {SUPPORTED.map((code) => (
                  <button
                    key={code}
                    type="button"
                    onClick={() => { switchLang(code); setOpen(false); }}
                    data-testid={`mlang-${code}`}
                    className={`px-3 py-1.5 rounded-full text-[12px] font-semibold uppercase tracking-wider ${
                      lang === code
                        ? "bg-[color:var(--z-ink)] text-[#F7F2E7]"
                        : "text-[color:var(--z-ink)]"
                    }`}
                  >
                    {code}
                  </button>
                ))}
              </div>
            </div>
            <Link
              to={langPath(lang) + "#contact"}
              onClick={() => setOpen(false)}
              className="z-btn z-btn-primary mt-4"
              data-testid="mheader-cta"
            >
              {t("hero.ctaPrimary")}
            </Link>
          </div>
        </div>
      )}
    </header>
  );
}
